"use client";

import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

interface AnzsicOption {
  value: string;
  label: string;
  code?: string;
  description?: string;
}

export interface AnzsicSearchComboboxProps {
  selected?: string | string[] | null;
  onSelect: (value: string) => void;
  onDeselect: (value: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  disabled?: boolean;
  placeholder?: string;
  singleSelect?: boolean;
  className?: string;
}

export function AnzsicSearchCombobox({
  selected = [],
  onSelect,
  onDeselect,
  onAllSelect,
  onAllDeselect,
  disabled = false,
  placeholder = "Search ANZSIC codes...",
  singleSelect = false,
  className,
}: AnzsicSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [options, setOptions] = useState<AnzsicOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOptionsCache, setSelectedOptionsCache] = useState<AnzsicOption[]>([]);
  
  // Test data for debugging
  const testOptions: AnzsicOption[] = [
    { value: 'test1', label: 'Test Option 1', code: '1234', description: 'Test Description 1' },
    { value: 'test2', label: 'Test Option 2', code: '5678', description: 'Test Description 2' },
  ];
  
  // Debug effect to log selected values changes
  useEffect(() => {
    // console.log('Selected values changed:', { selected, options });
  }, [selected, options]);

  // Fetch ANZSIC options
  const fetchAnzsicOptions = useCallback(async (query: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await deiterateAPI.GET<Array<{
        code: string;
        label: string;
        url: string;
        description?: string;
      }>>(`/services/anzsic?q=${encodeURIComponent(query)}`);
      
      // Map the API response to the expected AnzsicOption format
      const mappedOptions = Array.isArray(response) 
        ? response.map(item => ({
            value: item.code,
            label: item.label,
            code: item.code,
            description: item.description
          }))
        : [];
      
      setOptions(mappedOptions);
    } catch (err) {
      console.error("Error fetching ANZSIC codes:", err);
      setError("Failed to load ANZSIC codes");
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch individual ANZSIC code details
  const fetchAnzsicCodeDetails = useCallback(async (codes: string[]) => {
    try {
      const details = await Promise.all(
        codes.map(async (code) => {
          try {
            const response = await deiterateAPI.GET<Array<{
              code: string;
              label: string;
              url: string;
              description?: string;
            }>>(`/services/anzsic?q=${encodeURIComponent(code)}`);
            
            if (Array.isArray(response) && response.length > 0) {
              const item = response[0];
              return {
                value: item.code,
                label: item.label,
                code: item.code,
                description: item.description
              };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching details for code ${code}:`, err);
            return null;
          }
        })
      );
      
      return details.filter(Boolean) as AnzsicOption[];
    } catch (err) {
      console.error("Error fetching ANZSIC code details:", err);
      return [];
    }
  }, []);

  // Debounce the search with 750ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 750);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch when debounced search term changes
  useEffect(() => {
    // console.log('Fetching options for term:', debouncedSearchTerm);
    if (debouncedSearchTerm !== '') {
      fetchAnzsicOptions(debouncedSearchTerm);
    } else {
      // If search is cleared, fetch initial entries
      fetchAnzsicOptions('');
    }
  }, [debouncedSearchTerm, fetchAnzsicOptions]);
  
  // Debug options changes
  useEffect(() => {
    // console.log('Options updated:', options);
    // For testing: uncomment to use test data
    // setOptions(testOptions);
  }, [options]);

  // Initial load
  useEffect(() => {
    // console.log('Initial load');
    // For testing: uncomment to use test data
    // setOptions(testOptions);
    fetchAnzsicOptions("");
  }, [fetchAnzsicOptions]);

  // Safe selected values array
  const safeSelected = React.useMemo(() => {
    if (!selected) return [];
    const sel = Array.isArray(selected) ? selected : [selected].filter(Boolean);
    // console.log('Safe selected values:', { selected, safe: sel });
    return sel;
  }, [selected]);

  // Update selected options cache when selected values change
  useEffect(() => {
    const updateSelectedCache = async () => {
      if (!safeSelected.length) {
        setSelectedOptionsCache([]);
        return;
      }

      // Find which selected values are not in current options
      const missingValues = safeSelected.filter(value => 
        !options.find(opt => opt.value === value) &&
        !selectedOptionsCache.find(cached => cached.value === value)
      );

      if (missingValues.length > 0) {
        // Fetch details for missing selected values
        const fetchedDetails = await fetchAnzsicCodeDetails(missingValues);
        
        // Update cache with new details
        setSelectedOptionsCache(prev => {
          const existing = prev.filter(opt => safeSelected.includes(opt.value));
          return [...existing, ...fetchedDetails];
        });
      } else {
        // Clean up cache to only include currently selected values
        setSelectedOptionsCache(prev => 
          prev.filter(opt => safeSelected.includes(opt.value))
        );
      }
    };

    updateSelectedCache();
  }, [safeSelected, options, fetchAnzsicCodeDetails]);

  const handleSelect = React.useCallback((value: string) => {
    // console.log('Handling select:', { value, currentSelected: selected, safeSelected });
    if (safeSelected.includes(value)) {
      // console.log('Deselecting value:', value);
      onDeselect(value);
    } else {
      // console.log('Selecting value:', value);
      onSelect(value);
      if (singleSelect) setOpen(false);
    }
  }, [selected, safeSelected, onSelect, onDeselect, singleSelect]);

  // Get selected labels for display
  const selectedLabels = useMemo(() => {
    if (!safeSelected.length) {
      // console.log('No selected values');
      return [];
    }
    const labels = safeSelected.map(val => {
      // First try to find in current options
      let option = options.find(opt => opt.value === val);
      // If not found, try to find in cached selected options
      if (!option) {
        option = selectedOptionsCache.find(opt => opt.value === val);
      }
      // If still not found, use fallback
      const label = option ? `${option.code} - ${option.label}` : val;
      // console.log('Mapping selected value to label:', { value: val, label, found: !!option });
      return label;
    });
    // console.log('Selected labels:', labels);
    return labels;
  }, [safeSelected, options, selectedOptionsCache]);
  
  // Get selected options for display
  const selectedOptions = useMemo(() => {
    return safeSelected.map(value => {
      // First try to find in current options
      let option = options.find(opt => opt.value === value);
      // If not found, try to find in cached selected options
      if (!option) {
        option = selectedOptionsCache.find(opt => opt.value === value);
      }
      // If still not found, create a fallback option
      if (!option) {
        option = { value, label: value, code: value, description: 'Loading...' };
      }
      return option;
    });
  }, [safeSelected, options, selectedOptionsCache]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-auto min-h-10 py-2",
            !selectedLabels.length && "text-muted-foreground"
          )}
          onClick={() => setOpen(!open)}
          disabled={disabled}
        >
          <div className="flex-1 text-left">
            {selectedOptions.length > 0 ? (
              <div className="space-y-1">
                {selectedOptions.map((option, index) => (
                  <div key={option.value} className="flex items-center">
                    <span className="font-medium">{option.code}</span>
                    <span className="ml-2 text-muted-foreground truncate">
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start" side="bottom" alignOffset={5}>
        <Command shouldFilter={false} className="rounded-md border">
          <CommandInput
            placeholder={placeholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            disabled={disabled}
          />
          <CommandList className="max-h-[400px] overflow-auto" onWheel={(e) => e.stopPropagation()}>
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : error ? (
              <CommandEmpty className="py-2 px-4 text-sm">{error}</CommandEmpty>
            ) : options.length === 0 ? (
              <CommandEmpty className="py-2 px-4 text-sm">No ANZSIC codes found.</CommandEmpty>
            ) : (
              <CommandGroup heading="ANZSIC Codes">
                {options.map((opt) => {
                  const isSelected = safeSelected.includes(opt.value);
                  // console.log('Rendering option:', { opt, isSelected });
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      onSelect={() => {
                        // console.log('CommandItem onSelect:', opt.value);
                        handleSelect(opt.value);
                      }}
                      className={cn(
                        "flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-accent/50"
                      )}
                      disabled={singleSelect && isSelected && !safeSelected.includes(opt.value)}
                    >
                      <div 
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center",
                          isSelected ? "text-primary" : "text-muted-foreground"
                        )}
                      >
                        <Check
                          className={cn(
                            "h-4 w-4 flex-shrink-0 transition-opacity",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                          aria-hidden="true"
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium truncate">{opt.label}</span>
                        {opt.code && (
                          <span className="text-xs text-muted-foreground truncate">
                            {opt.code} • {opt.description || 'No description'}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default AnzsicSearchCombobox;
